import React, { useEffect, useRef } from 'react';

interface ShopifyBuyButtonProps {
  productId: string;
  componentId: string;
  domain: string;
  storefrontAccessToken: string;
  moneyFormat?: string;
  className?: string;
  price?: string;
  iframeTitle?: string;
}

const ShopifyBuyButton: React.FC<ShopifyBuyButtonProps> = ({
  productId,
  componentId,
  domain,
  storefrontAccessToken,
  moneyFormat = '%7B%7Bamount_with_comma_separator%7D%7D%20kr',
  className = '',
  price = '399 kr',
  iframeTitle = 'Shopify product buy button'
}) => {
  const componentRef = useRef<any>(null);

  useEffect(() => {
    const scriptURL = 'https://sdks.shopifycdn.com/buy-button/latest/buy-button-storefront.min.js';

    // Always clear old cached cart data to prevent stale discounts
    const clearOldCartCache = () => {
      try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.includes('shopify-buy') || key.includes('shopifyCheckout') || key.includes('shopify_checkout'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        if (keysToRemove.length > 0) {
          console.log('Cleared Shopify cart cache:', keysToRemove);
        }
      } catch (e) {
        console.warn('Could not clear cart cache:', e);
      }
    };

    // Always clear cache to remove any stale discount codes
    clearOldCartCache();

    const initialize = () => {
      const node = document.getElementById(componentId);
      if (!node) return;

      // Destroy any previous instance and clear the container
      if (componentRef.current && typeof componentRef.current.destroy === 'function') {
        componentRef.current.destroy();
        componentRef.current = null;
      }
      node.innerHTML = '';

      const client = (window as any).ShopifyBuy.buildClient({
        domain,
        storefrontAccessToken,
      });

      (window as any).ShopifyBuy.UI.onReady(client).then((ui: any) => {
        const component = ui.createComponent('product', {
          id: productId,
          node,
          moneyFormat,
          options: {
            product: {
              styles: {
                product: {
                  '@media (min-width: 601px)': {
                    'max-width': '100%',
                    'margin-left': '0px',
                    'margin-bottom': '0px',
                  },
                  'text-align': 'left',
                },
                button: {
                  'font-family': 'inherit',
                  'font-size': '16px',
                  'padding-top': '16px',
                  'padding-bottom': '16px',
                  'padding-left': '32px',
                  'padding-right': '32px',
                  'background-color': '#22c55e',
                  color: 'white',
                  'border-radius': '6px',
                  border: 'none',
                  'font-weight': '600',
                  ':hover': {
                    'background-color': '#16a34a',
                  },
                  ':focus': {
                    'background-color': '#16a34a',
                  },
                },
              },
              contents: {
                img: false,
                title: false,
                price: false,
              },
              text: {
                button: 'Legg til i handlekurv',
              },
            },
            productSet: {
              styles: {
                products: {
                  '@media (min-width: 601px)': {
                    'margin-left': '0px',
                  },
                },
              },
            },
            modalProduct: {
              contents: {
                img: false,
                imgWithCarousel: true,
                button: false,
                buttonWithQuantity: false,
              },
              styles: {
                product: {
                  '@media (min-width: 601px)': {
                    'max-width': '100%',
                    'margin-left': '0px',
                    'margin-bottom': '0px',
                  },
                },
              },
              text: {
                button: 'Legg til i handlekurv',
              },
            },
            option: {},
            cart: {
              text: {
                total: 'Delsum',
                button: 'Til kassen',
                notice: '🚚 Gratis frakt ved kjøp over 800 kr! Frakt og rabattkoder beregnes ved utsjekking.',
                noteDescription: 'Spesielle instruksjoner for bestillingen',
                empty: 'Handlekurven din er tom.',
                noteButton: 'Legg til notat',
                quantityLabel: 'Antall',
                remove: 'Fjern',
                title: 'Handlekurv',
              },
              styles: {
                cart: {
                  'text-align': 'left',
                },
                lineItems: {
                  // Hide discount displays - only show at checkout
                  '.shopify-buy__cart-item__discount': {
                    'display': 'none',
                  },
                },
                discount: {
                  'display': 'none',
                },
                discountText: {
                  'display': 'none',
                },
                discountIcon: {
                  'display': 'none',
                },
              },
              events: {
                afterRender: (cart: any) => {
                  // Add empty cart button if it doesn't exist
                  const cartFooter = document.querySelector('.shopify-buy__cart-bottom');
                  if (cartFooter && !document.getElementById('empty-cart-btn')) {
                    const emptyBtn = document.createElement('button');
                    emptyBtn.id = 'empty-cart-btn';
                    emptyBtn.textContent = 'Tøm handlekurv';
                    emptyBtn.style.cssText = 'width: 100%; padding: 10px; margin-top: 10px; background: transparent; border: 1px solid #999; border-radius: 6px; cursor: pointer; font-size: 14px; color: #666;';
                    emptyBtn.onmouseover = () => { emptyBtn.style.background = '#f5f5f5'; };
                    emptyBtn.onmouseout = () => { emptyBtn.style.background = 'transparent'; };
                    emptyBtn.onclick = () => {
                      if (cart && cart.model && cart.model.lineItems) {
                        const lineItems = cart.model.lineItems;
                        lineItems.forEach((item: any) => {
                          cart.updateItem(item.id, 0);
                        });
                      }
                    };
                    cartFooter.appendChild(emptyBtn);
                  }
                },
              },
            },
            toggle: {},
          },
        });
        componentRef.current = component;
      });
    };

    const loadScript = () => {
      if ((window as any).ShopifyBuy && (window as any).ShopifyBuy.UI) {
        initialize();
        return;
      }

      let script = document.getElementById('shopify-buy-button-script') as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = 'shopify-buy-button-script';
        script.async = true;
        script.src = scriptURL;
        (document.head || document.body).appendChild(script);
      }
      script.addEventListener('load', initialize, { once: true });
    };

    loadScript();

    return () => {
      if (componentRef.current && typeof componentRef.current.destroy === 'function') {
        componentRef.current.destroy();
        componentRef.current = null;
      }
      const node = document.getElementById(componentId);
      if (node) node.innerHTML = '';
    };
  }, [productId, componentId, domain, storefrontAccessToken, moneyFormat]);

  // Add title attribute to iframe after Shopify SDK creates it
  useEffect(() => {
    const addTitleToIframe = () => {
      const node = document.getElementById(componentId);
      if (node) {
        const iframe = node.querySelector('iframe');
        if (iframe && !iframe.hasAttribute('title')) {
          iframe.setAttribute('title', iframeTitle);
        }
      }
    };

    // Wait for iframe to be created by Shopify SDK
    const timeoutId = setTimeout(addTitleToIframe, 500);
    
    // Also try multiple times in case of delays
    const intervalId = setInterval(addTitleToIframe, 1000);
    
    // Clear after 5 seconds
    const clearIntervalId = setTimeout(() => clearInterval(intervalId), 5000);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
      clearTimeout(clearIntervalId);
    };
  }, [componentId, iframeTitle]);

  return <div id={componentId} className={className}></div>;
};

// Extend Window interface for TypeScript
declare global {
  interface Window {
    ShopifyBuy: any;
  }
}

export default ShopifyBuyButton;